import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
  Plus, Upload, FileDown, Paperclip, Loader2, Lock, PencilLine, Wallet, AlertTriangle,
  Link2, Link2Off, Copy, Check, QrCode, Users, X, Download, Printer, FolderInput,
} from 'lucide-react';
import { EinweisungOnlineTeilnahme, SigeEinweisung } from '../sige-plan/types';
import {
  aushangVorgabeEinweisung,
  beschreibeQuelle,
  darfLinkAktivieren,
  einweisungAushangDateiname,
  einweisungLinkUrl,
  einweisungQrDateiname,
  einweisungTitel,
  formatDatum,
  istGebucht,
  linkIstAktiv,
  zaehleTeilnahmen,
} from './einweisungLogic';
import {
  LINK_ENTWURF_HINWEIS,
  aushangFussTitel,
  beschreibeTeilnehmerZahl,
  zeigeTeilnahmenKnopf,
} from './onlineTeilnahmenLogic';
import { erzeugeAushangPdf } from '../../services/sifa/aushangPdf';
import { ensureEmbeddedFont } from '../../services/pdf/embeddedFont';
import { describeError } from '../../services/errorMessage';
import { anker } from '../../tutorial/anker';

interface Props {
  einweisungen: SigeEinweisung[];
  laden: boolean;
  fehler: string | null;
  isReadOnly?: boolean;
  isMobile?: boolean;
  /** Laufender Vorgang (anlegen). Sperrt die beiden anlegenden Knoepfe. */
  busy?: boolean;
  /** Einweisung, deren Dokument gerade geoeffnet wird. */
  dokumentBusyId?: string | null;
  onOeffnen: (e: SigeEinweisung) => void;
  onNeu: () => void;
  onPapierUpload: () => void;
  onPdf: (e: SigeEinweisung) => void;
  onDokument: (e: SigeEinweisung) => void;
  /**
   * Der ZWEITE Einstieg in die Abrechnungsfrage (siehe darfNachbuchen).
   * Ohne ihn kaeme sie genau einmal, und ein weggeklickter Dialog haette die
   * Einweisung still aus der Abrechnung fallen lassen.
   */
  kannAbrechnen?: (e: SigeEinweisung) => boolean;
  onAbrechnen?: (e: SigeEinweisung) => void;
  /** Einweisung, deren Abrechnung gerade laeuft. */
  buchungBusyId?: string | null;
  /** Online-Teilnahmen je Einweisung (useEinweisungen.onlineJeEinweisung). */
  onlineJeEinweisung?: Record<string, EinweisungOnlineTeilnahme[]>;
  /** Einweisung, deren Link gerade umgeschaltet wird. */
  linkBusyId?: string | null;
  /** Link aktiv/aus schalten — laeuft ueber die RPC (Container). */
  onLinkSchalten?: (e: SigeEinweisung, aktiv: boolean) => void;
  /** Oeffnet die Teilnahmen-Ansicht dieser Einweisung. */
  onOnlineTeilnahmen?: (e: SigeEinweisung) => void;
  /**
   * In die Projektablage („Dokumente") legen — von Hand, fuer abgeschlossene
   * Blaetter (darfEinweisungAblegen). Der Weg fuer Nachzuegler per QR: das
   * beim Abschliessen abgelegte Blatt kennt sie noch nicht.
   */
  kannAblegen?: (e: SigeEinweisung) => boolean;
  onAblegen?: (e: SigeEinweisung) => void;
  /** Einweisung, die gerade abgelegt wird. */
  ablageBusyId?: string | null;
  /** Bauvorhaben, steht auf dem Aushang. */
  projektName?: string;
  /** Betreuendes Buero (Fusszeile des Aushangs), z. B. „GefAS mbH". */
  bueroName?: string;
}

const KEINE_ONLINE: EinweisungOnlineTeilnahme[] = [];

/**
 * Die Ablage: alle Einweisungen dieses Projekts.
 *
 * Ein Projekt hat MEHRERE Einweisungen — bei jedem neuen Gewerk, nach jeder
 * Planfortschreibung, fuer jede Nachunternehmerkolonne. Sie bleiben deshalb
 * dauerhaft abrufbar und werden nie ueberschrieben; die laufende Nummer ist
 * das Aktenzeichen (siehe naechsteEinweisungsNummer).
 *
 * ─── Online-Einweisung per Link/QR ────────────────────────────────────────
 * Ein ABGESCHLOSSENES Blatt bekommt auf Knopfdruck einen Link
 * (`#/einweisung/<token>`); wer ihn scannt, liest die Punkte und
 * unterschreibt auf dem eigenen Handy. Der Link laesst sich nur auf ein
 * eingefrorenes Blatt legen (Plan §1.2) — im Entwurf ist der Knopf grau.
 * Die Teilnehmerzahl zaehlt Geraet + online („7 (3 online)").
 */
export const EinweisungListe: React.FC<Props> = ({
  einweisungen,
  laden,
  fehler,
  isReadOnly = false,
  isMobile = false,
  busy = false,
  dokumentBusyId = null,
  onOeffnen,
  onNeu,
  onPapierUpload,
  onPdf,
  onDokument,
  kannAbrechnen,
  onAbrechnen,
  buchungBusyId = null,
  onlineJeEinweisung,
  linkBusyId = null,
  onLinkSchalten,
  onOnlineTeilnahmen,
  kannAblegen,
  onAblegen,
  ablageBusyId = null,
  projektName,
  bueroName,
}) => {
  const aktion = 'min-h-[44px] px-3 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40';
  const [kopiertId, setKopiertId] = useState<string | null>(null);
  const [qrFuer, setQrFuer] = useState<SigeEinweisung | null>(null);

  const onlineVon = (e: SigeEinweisung) => onlineJeEinweisung?.[e.id] ?? KEINE_ONLINE;
  const standVon = (e: SigeEinweisung) => zaehleTeilnahmen(e, onlineVon(e));

  /** Der oeffentliche Link — nur, solange er nutzbar ist. */
  const linkVon = (e: SigeEinweisung) =>
    linkIstAktiv(e) && e.link_token
      ? einweisungLinkUrl(window.location.origin, window.location.pathname, e.link_token)
      : null;

  const kopieren = async (e: SigeEinweisung) => {
    const url = linkVon(e);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setKopiertId(e.id);
      window.setTimeout(() => setKopiertId((id) => (id === e.id ? null : id)), 2000);
    } catch {
      // Zwischenablage gesperrt (unsicherer Kontext): der Link kommt zum Markieren.
      window.prompt('Link kopieren:', url);
    }
  };

  const statusChip = (e: SigeEinweisung) =>
    e.status === 'abgeschlossen' ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full">
        <Lock className="w-3 h-3" /> Abgeschlossen
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full">
        <PencilLine className="w-3 h-3" /> Entwurf
      </span>
    );

  const buchungChip = (e: SigeEinweisung) =>
    istGebucht(e) ? (
      <span title={`Gebucht am ${formatDatum(e.gebucht_am)}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded-full">
        <Wallet className="w-3 h-3" /> gebucht
      </span>
    ) : null;

  /** Nur bei abgeschlossenen Blaettern — im Entwurf gibt es keinen Link. */
  const linkChip = (e: SigeEinweisung) => {
    if (e.status !== 'abgeschlossen') return null;
    return linkIstAktiv(e) ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full">
        <Link2 className="w-3 h-3" /> Link aktiv
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full">
        <Link2Off className="w-3 h-3" /> Link aus
      </span>
    );
  };

  const chips = (e: SigeEinweisung) => (
    <>{statusChip(e)}{linkChip(e)}{buchungChip(e)}</>
  );

  const teilnehmerText = (e: SigeEinweisung) => {
    const z = standVon(e);
    return (
      <>
        {z.online > 0 ? (
          <>
            {z.gesamt} <span className="text-teal-700">({z.online} online)</span>
          </>
        ) : z.gesamt}
        {z.unterschrieben > 0 && (
          <span className="text-slate-400"> · {z.unterschrieben} unterschr.</span>
        )}
      </>
    );
  };

  const aktionen = (e: SigeEinweisung, istErsteZeile = false) => {
    const aktiv = linkIstAktiv(e);
    const url = linkVon(e);
    const z = standVon(e);
    const darfAktiv = darfLinkAktivieren(e);
    return (
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => onOeffnen(e)} className={`${aktion} text-blue-700 bg-blue-50 hover:bg-blue-100`}>
          {e.status === 'abgeschlossen' || isReadOnly ? 'Ansehen' : 'Öffnen'}
        </button>
        <button onClick={() => onPdf(e)} className={`${aktion} text-slate-700 bg-slate-100 hover:bg-slate-200`}>
          <FileDown className="w-3.5 h-3.5" /> PDF
        </button>
        {e.dokument_pfad && (
          <button
            onClick={() => onDokument(e)}
            disabled={dokumentBusyId === e.id}
            title={e.dokument_name || 'Hinterlegtes Dokument'}
            className={`${aktion} text-violet-700 bg-violet-50 hover:bg-violet-100`}
          >
            {dokumentBusyId === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
            Dokument
          </button>
        )}
        {onAbrechnen && kannAbrechnen?.(e) && (
          <button
            onClick={() => onAbrechnen(e)}
            disabled={buchungBusyId === e.id}
            title="Die Abrechnungsfrage nachholen"
            className={`${aktion} text-emerald-700 bg-emerald-50 hover:bg-emerald-100`}
          >
            {buchungBusyId === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
            Abrechnen
          </button>
        )}
        {onLinkSchalten && (
          <button
            {...anker('einweisung.link-aktivieren', istErsteZeile)}
            onClick={() => onLinkSchalten(e, !aktiv)}
            disabled={isReadOnly || linkBusyId === e.id || (!aktiv && !darfAktiv)}
            title={
              isReadOnly
                ? 'Nur-Ansicht: der Link wird nicht geschaltet'
                : !aktiv && !darfAktiv
                  ? LINK_ENTWURF_HINWEIS
                  : aktiv
                    ? 'Link deaktivieren — der ausgehängte QR-Code wird ungültig'
                    : 'Link für die Online-Einweisung aktivieren'
            }
            className={`${aktion} ${aktiv ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' : 'text-teal-700 bg-teal-50 hover:bg-teal-100'}`}
          >
            {linkBusyId === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : aktiv ? <Link2Off className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {aktiv ? 'Link aus' : 'Link'}
          </button>
        )}
        {url && (
          <>
            <button {...anker('einweisung.link-kopieren', istErsteZeile)} onClick={() => void kopieren(e)} className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
              {kopiertId === e.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              {kopiertId === e.id ? 'Kopiert' : 'Kopieren'}
            </button>
            <button {...anker('einweisung.qr', istErsteZeile)} onClick={() => setQrFuer(e)} className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
              <QrCode className="w-3.5 h-3.5" /> QR
            </button>
          </>
        )}
        {onOnlineTeilnahmen && zeigeTeilnahmenKnopf(e, z) && (
          <button {...anker('einweisung.teilnahmen', istErsteZeile)} onClick={() => onOnlineTeilnahmen(e)} className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
            <Users className="w-3.5 h-3.5" /> Teilnahmen{z.online > 0 ? ` (${z.online})` : ''}
          </button>
        )}
        {onAblegen && kannAblegen?.(e) && (
          <button
            onClick={() => onAblegen(e)}
            disabled={ablageBusyId === e.id}
            title={z.online > 0
              ? 'Blatt mit allen Online-Teilnahmen in „Dokumente" ablegen'
              : 'Blatt in „Dokumente" ablegen (derselbe Stand wird nicht doppelt abgelegt)'}
            className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}
          >
            {ablageBusyId === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />}
            Ablegen
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Einweisungen</h2>
          <p className="text-xs text-slate-500">
            {einweisungen.length === 0 ? 'Noch keine Einweisung abgelegt' : `${einweisungen.length} abgelegt`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onPapierUpload}
            disabled={isReadOnly || busy}
            title={isReadOnly ? 'Nur-Ansicht: kein Upload möglich' : undefined}
            className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-40"
          >
            <Upload className="w-4 h-4" /> Papier hochladen
          </button>
          <button
            onClick={onNeu}
            disabled={isReadOnly || busy}
            title={isReadOnly ? 'Nur-Ansicht: keine neue Einweisung' : undefined}
            className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Neue Einweisung
          </button>
        </div>
      </div>

      {fehler && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span>{fehler}</span>
        </div>
      )}

      {laden ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : einweisungen.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
          <p className="text-sm text-slate-600 font-semibold">Noch keine Einweisung</p>
          <p className="text-xs text-slate-500 mt-1">
            „Neue Einweisung" legt ein Blatt an und schlägt die Punkte aus dem SiGe-Plan vor.
            Eine auf Papier gehaltene Einweisung lässt sich als Scan oder Foto hinterlegen.
            Nach dem Abschließen lässt sich ein Link aktivieren, über den Teilnehmer per QR-Code
            online lesen und unterschreiben.
          </p>
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {einweisungen.map((e, zeilenIndex) => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">Nr. {e.nummer} — {einweisungTitel(e)}</p>
                  <p className="text-xs text-slate-500">
                    {formatDatum(e.datum)}{e.uhrzeit ? `, ${e.uhrzeit}` : ''} · {beschreibeQuelle(e)}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {e.durchfuehrender_name || 'ohne Durchführenden'} · {beschreibeTeilnehmerZahl(standVon(e))} Teilnehmer
                  </p>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">{chips(e)}</div>
              </div>
              {aktionen(e, zeilenIndex === 0)}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left font-bold">Nr.</th>
                <th className="px-4 py-2 text-left font-bold">Datum</th>
                <th className="px-4 py-2 text-left font-bold">Durchführender</th>
                <th className="px-4 py-2 text-left font-bold">Teilnehmer</th>
                <th className="px-4 py-2 text-left font-bold">Quelle</th>
                <th className="px-4 py-2 text-left font-bold">Status</th>
                <th className="px-4 py-2 text-left font-bold">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {einweisungen.map((e, zeilenIndex) => (
                <tr key={e.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-2 font-bold text-slate-800">{e.nummer}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatDatum(e.datum)}
                    {e.uhrzeit ? <span className="text-slate-400">, {e.uhrzeit}</span> : null}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{e.durchfuehrender_name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{teilnehmerText(e)}</td>
                  <td className="px-4 py-2 text-slate-600">{beschreibeQuelle(e)}</td>
                  <td className="px-4 py-2"><div className="flex flex-col items-start gap-1">{chips(e)}</div></td>
                  <td className="px-4 py-2">{aktionen(e, zeilenIndex === 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {qrFuer && (
        <QrDialog
          einweisung={qrFuer}
          url={linkVon(qrFuer) || ''}
          projektName={projektName || ''}
          bueroName={bueroName || ''}
          onClose={() => setQrFuer(null)}
        />
      )}
    </div>
  );
};

/**
 * QR-Code zum Einweisungslink — als PNG (E-Mail, Bautagebuch) und als
 * AUSHANG (A4-PDF mit Ueberschrift, Kurztext und grossem Code) fuer den
 * Container, das Baustellenbuero, das Schwarze Brett. Das Bild wird erst hier
 * erzeugt, damit die Bibliothek nur laeuft, wenn jemand den Knopf drueckt.
 *
 * Ueberschrift und Text sind vorbelegt (aushangVorgabeEinweisung) und vor dem
 * Druck frei aenderbar; gespeichert werden sie nicht — der Aushang ist ein
 * Druckerzeugnis, kein Datensatz. Muster: components/sifa/UnterweisungListe.
 */
const QrDialog: React.FC<{
  einweisung: SigeEinweisung;
  url: string;
  projektName: string;
  bueroName: string;
  onClose: () => void;
}> = ({ einweisung, url, projektName, bueroName, onClose }) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [vorgabe] = useState(() => aushangVorgabeEinweisung(einweisung, projektName));
  const [ueberschrift, setUeberschrift] = useState(vorgabe.ueberschrift);
  const [text, setText] = useState(vorgabe.text);
  const [druckLaeuft, setDruckLaeuft] = useState(false);

  useEffect(() => {
    let aktiv = true;
    QRCode.toDataURL(url, { width: 480, margin: 2 })
      .then((d) => { if (aktiv) setDataUrl(d); })
      .catch((e) => { if (aktiv) setFehler(String(e?.message || e)); });
    return () => { aktiv = false; };
  }, [url]);

  const aushangErzeugen = async () => {
    setDruckLaeuft(true);
    setFehler(null);
    try {
      // Eigener, groesserer Code fuer den Druck: 480 px reichen fuer den
      // Bildschirm, auf 12 cm Papier wuerden sie weich.
      const qrDruck = await QRCode.toDataURL(url, { width: 1200, margin: 1 });
      await ensureEmbeddedFont();
      const bytes = erzeugeAushangPdf({
        ueberschrift,
        text,
        url,
        qrDataUrl: qrDruck,
        bueroName,
        kundeName: projektName,
        artLabel: 'Einweisung',
        unterweisungNummer: einweisung.nummer,
        unterweisungTitel: aushangFussTitel(einweisung),
      });
      const { saveAs } = await import('file-saver');
      saveAs(new Blob([bytes], { type: 'application/pdf' }), einweisungAushangDateiname(einweisung));
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setDruckLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">QR-Code zur Online-Einweisung</h3>
            <p className="text-xs text-slate-500 truncate">Nr. {einweisung.nummer} — {einweisungTitel(einweisung)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4">
          <div className="space-y-2">
            {fehler ? (
              <p className="text-xs text-red-700">{fehler}</p>
            ) : dataUrl ? (
              <img data-tutorial="einweisung.qr-bild" src={dataUrl} alt="QR-Code" className="w-full max-w-[200px] mx-auto rounded-lg border border-slate-200" />
            ) : (
              <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
            )}
            <p className="text-[10px] text-slate-400 break-all select-all text-center">{url}</p>
            {dataUrl && (
              <a
                href={dataUrl}
                download={einweisungQrDateiname(einweisung)}
                className="min-h-[40px] w-full flex items-center justify-center gap-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4" /> Nur QR als PNG
              </a>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Aushang (A4)</p>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Überschrift</span>
              <input
                value={ueberschrift}
                onChange={(e) => setUeberschrift(e.target.value)}
                maxLength={120}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-200"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Kurzer Text</span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                maxLength={600}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-200 resize-y"
              />
              <span className="block text-[10px] text-slate-400 mt-1">Leerzeile = neuer Absatz. Der Link steht zusätzlich als Text unter dem Code.</span>
            </label>
            <button
              data-tutorial="einweisung.aushang-pdf"
              type="button"
              onClick={() => void aushangErzeugen()}
              disabled={druckLaeuft || !url}
              className="min-h-[44px] w-full flex items-center justify-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-xl transition-colors"
            >
              {druckLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Aushang (A4) herunterladen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
